/**
 * Break the FALSE circular dependency Univer builds from a whole-column reference
 * (`X:X`) inside a formula that itself lives in column `X`.
 *
 * THE BUG. The THM (and any summed-parameter) template's "Numeric Result" formula is
 *   `IFERROR(IF(D="THMs", SUMIFS(J:J,…)+…, IF(BC="","",VALUE(BC))), "")`
 * A component row (e.g. Chloroform) always takes the ELSE branch, so its result is the
 * per-row reading (empty on a fresh batch). But the SUMIFS in the untaken THEN branch
 * references the WHOLE column `J:J` — which includes the "THMs" summary row, whose OWN
 * SUMIFS sums the component rows. DevExpress evaluates the IF lazily, so a component
 * row's untaken SUMIFS is never a dependency and there is no cycle. Univer builds the
 * dependency graph STATICALLY from both branches, so component-row ⇄ summary-row form a
 * cycle. Univer breaks the cycle by resolving the cyclic cells to 0 — so the empty
 * Numeric Result is handed to the Rec%/Dev%/QCStatus formulas as 0, giving Rec%=0.0,
 * Dev%=-100.0 where OG is blank (and, worse, poisoning those cells even once a real
 * result is entered).
 *
 * THE FIX. Reproduce DevExpress's lazy evaluation: because the merge has already filled
 * the IF's condition cell (the Parameter) with a concrete value, we can constant-fold
 * the IF at conversion time — replacing `IF(cond, then, else)` with whichever branch the
 * (static) condition selects. We fold ONLY the narrow, cycle-causing case: an IF whose
 * UNTAKEN branch contains a self-column aggregate over this cell's own column. That
 * removes the dead whole-column reference (and the cycle) while leaving every other
 * formula, and the live per-row logic, byte-for-byte unchanged.
 */

/** A `(SUM|AVERAGE|COUNT|MAX|MIN)IF(S)?( X:X …` over the formula's OWN column `col` — whole-column
 *  (`J:J`) OR the row-bounded form (`J$1:J$268`). excelToUniver runs boundWholeColumnRefs BEFORE the
 *  fold, so by then every `J:J` reads `J$1:J$n`; matching only `J:J` left the fold dead since the
 *  bounding landed (2026-08-25) — the untaken VOC/THM "THMs" SUMIFS then kept the J column in a
 *  cycle and Univer left everything reading J uncalculated (VOC per-run AR / THM Summary AP blank
 *  where OG shows 0; parity harness 2026-09-26). */
function selfColumnAggregateRe(col: string): RegExp {
  return new RegExp(`\\b(?:SUM|AVERAGE|COUNT|MAX|MIN)IFS?\\s*\\(\\s*\\$?${col}\\$?\\d*\\s*:\\s*\\$?${col}\\$?\\d*(?![A-Z0-9])`, 'i');
}

/** Split the argument list that starts just after a `(` into top-level comma segments.
 *  `body` is the text from just-after the open paren; returns the segments and the index
 *  (within `body`) of the paren that closes the call. Quotes are respected. */
function splitArgs(body: string): { args: string[]; closeIdx: number } | null {
  const args: string[] = [];
  let depth = 0, start = 0, inStr = false;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (inStr) { if (ch === '"') { if (body[i + 1] === '"') i++; else inStr = false; } continue; }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '(') depth++;
    else if (ch === ')') { if (depth === 0) { args.push(body.slice(start, i)); return { args, closeIdx: i }; } depth--; }
    else if (ch === ',' && depth === 0) { args.push(body.slice(start, i)); start = i + 1; }
  }
  return null; // unbalanced
}

/** Resolve a simple `<CELLREF> = "LITERAL"` (or `<CELLREF>="LITERAL"`, or `<CELLREF> <> "…"`)
 *  condition against a cell-value resolver. Returns true/false, or null if not resolvable
 *  (unknown cell, non-simple condition) so the caller leaves the formula untouched. */
function evalSimpleEquality(cond: string, resolve: (ref: string) => string | number | boolean | null | undefined): boolean | null {
  const m = /^\s*(\$?[A-Z]{1,3}\$?\d+)\s*(=|<>)\s*"([^"]*)"\s*$/.exec(cond);
  if (!m) return null;
  const ref = m[1]!, op = m[2]!, lit = m[3]!;   // all three groups are mandatory in the regex
  const v = resolve(ref.replace(/\$/g, ''));
  if (v === undefined) return null;                         // cell unknown → don't fold
  const eq = String(v ?? '') === lit;
  return op === '=' ? eq : !eq;
}

/**
 * Fold dead self-column-aggregate IF branches in `fml` (a full `=…` formula string) for a
 * cell in column `selfCol`. `resolve(ref)` returns the merged value of a same-sheet cell
 * ref (or undefined if unknown/not a static value). Returns the possibly-rewritten formula;
 * returns the input UNCHANGED whenever anything is ambiguous — this only ever REMOVES a
 * provably-dead self-column aggregate branch, never changes a live one.
 */
/**
 * Rewrite the THM-family CROSS-SHEET "parent sample" lookup to an equivalent SAME-SHEET one.
 *
 * THE BUG. The THM template (8112) computes a QC row's parent-sample result (column AG,
 * "QCSample Result") by INDIRECT-ing into a PER-SAMPLE worksheet named by the SampleID:
 *   IF(OR(A="Dup",A="MS","LCSD"),
 *      IFERROR(INDEX(INDIRECT("'"&AF8&"'!AL8:AL100"),
 *                    MATCH(D8, INDIRECT("'"&AF8&"'!D8:D100"),0)), "No data"), "")
 * OG renders THMs as MANY sheets (one per sample, named by SampleID), so `'&AF8&'!` resolves.
 * AI renders ONE merged worksheet (sdmsMailMerge v1 expands only the main sheet), so every
 * INDIRECT("'<SampleID>'!…") points at a sheet that does not exist → #REF! → AG is blank →
 * the RPD% (N) and Rec% (O) formulas that consume AG are blank too (user feedback 2026-08-27:
 * THM Dup/MS rows show no RPD/Rec, where a component-based test would).
 *
 * THE FIX. On the merged sheet the parent row is simply the row whose SysSampleCode (A) equals
 * this row's SampleID (AF) — DB-verified: parent A = "…-002", its Dup's AF = "…-002", and the
 * Dup's OWN A = "…-002Dup1", so a Dup never self-matches (match on A, NOT AF — parent and Dup
 * SHARE AF). Replace the cross-sheet INDIRECT with a same-sheet AVERAGEIFS over the NumericResult
 * column (J), keyed on A = <this row's SampleID (the INDIRECT's own name ref)> AND
 * <Parameter col> = <this row's Parameter (the MATCH's own ref)>. Exactly one numeric parent row
 * per (sample,parameter) → AVERAGEIFS returns it; none/blank → IFERROR → "" (blank until a result
 * is entered, matching OG). This is the same same-sheet AVERAGEIFS shape the component-based
 * Metals template (1014) already uses natively. A/D/J are the fixed THM-family layout
 * (A=SysSampleCode, D=Parameter, J=NumericResult) — this rewrite fires ONLY on the cross-sheet
 * INDIRECT shape, which is unique to that family, so every other template is untouched.
 */
export function rewriteCrossSheetParentLookup(
  fml: string,
  /** Does the workbook have a sheet named by `ref`'s (static, merged) value? `true` → the INDIRECT
   *  resolves as written and is left alone. VOC 8086's Summary looks each run's result up in the RUN's
   *  own sheet (`INDEX(INDIRECT("'"&A10&"'!J8:J100"), MATCH(B10, …))`) — those sheets exist (Model 1)
   *  and Univer evaluates the cross-sheet INDIRECT exactly (7.54, probed); rewriting it to the THM
   *  same-sheet AVERAGEIFS blanked Summary H/J/K/L on every non-Dup row (parity harness 2026-09-24). */
  sheetExistsForRef?: (ref: string) => boolean | undefined,
): string {
  // A cross-sheet INDIRECT keyed on a SAME-ROW cell: INDIRECT("'" & <nameRef> & "'!<col><n>:…")
  const nm = /INDIRECT\s*\(\s*"'"\s*&\s*(\$?[A-Z]{1,3}\$?\d+)\s*&\s*"'!/i.exec(fml);
  if (!nm) return fml;                                     // not a cross-sheet INDIRECT → leave alone
  const nameRef = nm[1]!.replace(/\$/g, '');               // e.g. AF8 — this row's SampleID
  if (sheetExistsForRef?.(nameRef) === true) return fml;  // the target sheet exists → the INDIRECT works
  // MATCH(<matchRef>, INDIRECT("'"&…&"'!<matchCol><n>…"),0) → the parameter key + its column.
  const mm = /MATCH\s*\(\s*(\$?[A-Z]{1,3}\$?\d+)\s*,\s*INDIRECT\s*\(\s*"'"\s*&[^&]*&\s*"'!\s*\$?([A-Z]{1,3})\$?\d+/i.exec(fml);
  if (!mm) return fml;                                     // shape we don't recognise → leave alone
  const matchRef = mm[1]!.replace(/\$/g, '');              // e.g. D8 — this row's Parameter
  const matchCol = mm[2]!.replace(/\$/g, '');              // e.g. D  — the Parameter column
  const row = (/(\d+)$/.exec(nameRef) ?? ['', '8'])[1]!;   // this row number (from the name ref)
  // Gate with SEARCH (substring) on the FULL SysSampleCode so Dup/MS/LCSD rows qualify and
  // Sample/Blank/CCV rows return "" — the template's own AG gate used A="Dup" (EXACT), which
  // never matches the full code "…Dup1"; SEARCH is exactly what its N/O (RPD/Rec) cells use.
  return `=IF(OR(ISNUMBER(SEARCH("Dup",A${row})),ISNUMBER(SEARCH("MS",A${row})),ISNUMBER(SEARCH("LCSD",A${row}))),`
       + `IFERROR(AVERAGEIFS(J:J,A:A,${nameRef},${matchCol}:${matchCol},${matchRef}),""),"")`;
}

export function foldDeadSelfColumnBranches(
  fml: string,
  selfCol: string,
  resolve: (ref: string) => string | number | boolean | null | undefined,
): string {
  const aggRe = selfColumnAggregateRe(selfCol);
  if (!aggRe.test(fml)) return fml;                         // no self-column aggregate → nothing to do

  // Repeatedly fold the leftmost qualifying IF until none remain (handles nesting).
  for (let guard = 0; guard < 20; guard++) {
    let folded = false;
    const re = /\bIF\s*\(/gi;
    let mm: RegExpExecArray | null;
    while ((mm = re.exec(fml)) !== null) {
      const bodyStart = mm.index + mm[0].length;
      const parsed = splitArgs(fml.slice(bodyStart));
      if (!parsed || parsed.args.length !== 3) continue;    // not a 3-arg IF
      const cond = parsed.args[0]!, thenB = parsed.args[1]!, elseB = parsed.args[2]!;   // length === 3 checked above
      const thenHasAgg = aggRe.test(thenB), elseHasAgg = aggRe.test(elseB);
      if (thenHasAgg === elseHasAgg) continue;              // fold only when EXACTLY one branch has it
      const take = evalSimpleEquality(cond, resolve);
      if (take === null) continue;                          // condition not statically resolvable → leave it
      const keep = take ? thenB : elseB;
      const dropIsAgg = take ? elseHasAgg : thenHasAgg;
      if (!dropIsAgg) continue;                             // the DROPPED branch isn't the aggregate one → leave it
      // Replace `IF(<full>)` with the kept branch (trimmed).
      const ifEnd = bodyStart + parsed.closeIdx + 1;        // index just past the IF's ')'
      fml = fml.slice(0, mm.index) + keep.trim() + fml.slice(ifEnd);
      folded = true;
      break;                                                // restart the scan on the rewritten string
    }
    if (!folded) break;
  }
  return fml;
}

/**
 * Exclude a cell's OWN ROW from a self-column SUMIFS / COUNTIFS (the TRUE self-reference the fold above
 * cannot remove, because the branch is live).
 *
 * THE BUG. A summed-parameter template's SUMMARY row — THM 8112 "THMs" (J12 =
 * `SUMIFS(J:J, D:D, "Bromoform", A:A, A12) + …`), HAA 8075 "THAA5" (N: `SUMIFS(N:N, …)`) — totals its
 * own column, and the range covers its own cell: a circular reference. DevExpress tolerates it (the
 * summary row never matches the component criteria, so its own value never contributes). Univer
 * resolves the cycle to 0 — J12 = 0 where OG has 32.94 — AND the broken evaluation order leaves every
 * cell that reads the J column stale: THM Summary Y8 `IFERROR(VALUE(J8),0)` = 0 and AP8 blank while J8
 * itself shows 8.3, the same in HAA's AU / AV / O / S / QCStatus chain (parity harness, 2026-09-24).
 *
 * THE FIX. Split every range argument of such a call around the cell's own row — rows above + rows
 * below, summed. The own row cannot contribute to its own total, so the value is unchanged and the
 * cycle is gone. Applies only when every range argument is a single-column range with the SAME row
 * span that contains the own row; anything else is left exactly as written. `fml` has already been
 * through boundWholeColumnRefs (`J:J` → `J$1:J$n`).
 */
/*
 * AVERAGEIFS (2026-10-01). PFAS 533's spiked-duplicate parent value AJ = `AVERAGEIFS(AJ:AJ, B:B, "LFSM_L", …)` averages
 * its OWN column, whose matching rows are themselves formulas. Univer's first calculation then read the LFSMD row's
 * AJ as 0 for its Rec % (`(J - AJ) / M`) and never revisited it: 16 Rec % + 1 QCStatus wrong on every reopen
 * (QB26100101_001 4:2FTS 193 where OG has 179) until something forced a second full calculation. An average cannot
 * be split into two averages, so it becomes (sum above + sum below) / (count of NUMBERS above + below) — the
 * numbers counted as `">=0"` + `"<0"`, the cells AVERAGEIFS itself averages — with the own row left out as for
 * SUMIFS. No numbers → 0/0 → #DIV/0!, as AVERAGEIFS.
 */
export function excludeSelfRowFromSelfColumnAggregates(fml: string, selfCol: string, row: number): string {
  const RANGE_RE = /^\s*\$?([A-Z]{1,3})\$?(\d+)\s*:\s*\$?([A-Z]{1,3})\$?(\d+)\s*$/i;
  const calls: Array<{ start: number; end: number; text: string }> = [];
  const re = /\b(SUMIFS|COUNTIFS|AVERAGEIFS)\s*\(/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(fml)) !== null) {
    const fn = m[1]!.toUpperCase();
    const bodyStart = m.index + m[0].length;
    const parsed = splitArgs(fml.slice(bodyStart));
    if (!parsed) continue;
    const args = parsed.args;
    const rangeIdx = fn === 'SUMIFS' || fn === 'AVERAGEIFS'
      ? [0, ...args.map((_, i) => i).filter(i => i % 2 === 1)]
      : args.map((_, i) => i).filter(i => i % 2 === 0);
    const ranges = rangeIdx.map(i => RANGE_RE.exec(args[i] ?? ''));
    if (ranges.some(r => !r || r[1]!.toUpperCase() !== r[3]!.toUpperCase())) continue;   // not all single-column ranges
    const r1 = Number(ranges[0]![2]), r2 = Number(ranges[0]![4]);
    if (ranges.some(r => Number(r![2]) !== r1 || Number(r![4]) !== r2)) continue;          // spans differ
    if (row < r1 || row > r2) continue;                                                      // own row not covered
    if (!ranges.some(r => r![1]!.toUpperCase() === selfCol.toUpperCase())) continue;        // no range over the own column
    const rangeAt = (k: number, a: number, b: number) => `${ranges[k]![1]!.toUpperCase()}$${a}:${ranges[k]![1]!.toUpperCase()}$${b}`;
    const part = (a: number, b: number, name = fn) => `${name}(${args.map((arg, i) => {
      const k = rangeIdx.indexOf(i);
      return k < 0 ? arg : rangeAt(k, a, b);
    }).join(',')})`;
    const spans = [row > r1 ? [r1, row - 1] : null, row < r2 ? [row + 1, r2] : null].filter((s): s is number[] => !!s);
    if (!spans.length) continue;
    if (fn === 'AVERAGEIFS') {
      // The criteria pairs alone (args 1..), plus the average range counted where it holds a number.
      const crit = (a: number, b: number) => args.slice(1).map((arg, j) => {
        const k = rangeIdx.indexOf(j + 1);
        return k < 0 ? arg : rangeAt(k, a, b);
      }).join(',');
      const sums = spans.map(([a, b]) => part(a!, b!, 'SUMIFS'));
      const counts = spans.flatMap(([a, b]) => [`COUNTIFS(${crit(a!, b!)},${rangeAt(0, a!, b!)},">=0")`,
                                                 `COUNTIFS(${crit(a!, b!)},${rangeAt(0, a!, b!)},"<0")`]);
      calls.push({ start: m.index, end: bodyStart + parsed.closeIdx + 1, text: `((${sums.join('+')})/(${counts.join('+')}))` });
      continue;
    }
    const pieces = spans.map(([a, b]) => part(a!, b!));
    calls.push({ start: m.index, end: bodyStart + parsed.closeIdx + 1, text: `(${pieces.join('+')})` });
  }
  for (const c of calls.reverse()) fml = fml.slice(0, c.start) + c.text + fml.slice(c.end);
  return fml;
}

/**
 * Rewrite `<ref> = ""` / `<ref> <> ""` (and the mirrored `"" = <ref>`) to `LEN(<ref>)=0` / `LEN(<ref>)>0`.
 *
 * THE BUG. Univer 0.25 treats the empty string literal `""` in a comparison like an EMPTY CELL, so
 * `0 = ""` is TRUE (probed 2026-09-26: `=A1=""` → TRUE for A1 = 0, `=0`, `VALUE("0.00")` or
 * `IFERROR(VALUE("0.00"),"")`). Excel and DevExpress answer FALSE — a number never equals text.
 * Every template guard of the form `IF(J8="","",VALUE(J8))` therefore blanks a real 0 result: VOC
 * per-run AR / THM Summary AP show blank where OG shows 0 (~2,300 cells in the parity run).
 *
 * THE FIX. `LEN(x)=0` gives Excel's answer for every value `x=""` is used with: blank cell → TRUE,
 * `""` → TRUE, 0 → FALSE (LEN 1), text → FALSE, an error → the same error. Only a plain cell
 * reference (optionally sheet-qualified) compared with an exact `""` via `=` or `<>` is rewritten;
 * string literals are never touched, and `>=` / `<=` never match.
 */
// Built once, not per formula (speed, 2026-09-30 — this runs for every formula cell of a sheet). No `g` / `y`
// flag, so `exec` keeps no state between calls.
const EMPTY_CMP_REF = String.raw`(?:(?:'[^']+'|[A-Za-z_][\w.]*)!)?\$?[A-Z]{1,3}\$?\d+`;
// A single cell only: never the end of a RANGE (`AR8:AR51 <> ""` inside SUMPRODUCT is an array
// compare — rewriting its last cell produced `AR8:LEN(AR51)>0` = #REF!), so no `:` on either side.
const EMPTY_CMP_TAIL_RE = new RegExp(String.raw`(^|[^A-Za-z0-9_.$!':])(${EMPTY_CMP_REF})\s*(<>|=)\s*$`);   // …<ref> =   right before ""
const EMPTY_CMP_HEAD_RE = new RegExp(String.raw`^\s*(<>|=)\s*(${EMPTY_CMP_REF})(?![A-Za-z0-9_(!$':])`);    // = <ref>…   right after ""

export function rewriteEmptyStringComparisons(fml: string): string {
  if (!fml.includes('""')) return fml;
  // Split into code / string-literal parts (a doubled quote inside a literal is an escaped quote).
  const parts: Array<{ code: boolean; text: string }> = [];
  let i = 0, start = 0;
  while (i < fml.length) {
    if (fml[i] === '"') {
      if (i > start) parts.push({ code: true, text: fml.slice(start, i) });
      let j = i + 1;
      while (j < fml.length) { if (fml[j] === '"') { if (fml[j + 1] === '"') { j += 2; continue; } break; } j++; }
      parts.push({ code: false, text: fml.slice(i, j + 1) });
      i = j + 1; start = i;
    } else i++;
  }
  if (start < fml.length) parts.push({ code: true, text: fml.slice(start) });

  const tailRe = EMPTY_CMP_TAIL_RE, headRe = EMPTY_CMP_HEAD_RE;
  const lenOf = (ref: string, op: string) => `LEN(${ref})${op === '=' ? '=0' : '>0'}`;
  let changed = false;
  for (let k = 0; k < parts.length; k++) {
    const p = parts[k]!;
    if (p.code || p.text !== '""') continue;
    const prev = parts[k - 1], next = parts[k + 1];
    const t = prev?.code ? tailRe.exec(prev.text) : null;
    if (t) {
      prev!.text = prev!.text.slice(0, t.index) + t[1] + lenOf(t[2]!, t[3]!);
      p.text = ''; changed = true; continue;
    }
    // Mirrored `"" = <ref>` — only when the "" is itself an operand (after an operator, paren or comma).
    const h = next?.code ? headRe.exec(next.text) : null;
    if (h && (!prev || (prev.code && /[(,&+\-*/^=\s]$/.test(prev.text)))) {
      next!.text = lenOf(h[2]!, h[1]!) + next!.text.slice(h[0].length);
      p.text = ''; changed = true;
    }
  }
  return changed ? parts.map(x => x.text).join('') : fml;
}
